import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShieldIcon from "@mui/icons-material/Shield";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { EVOTRUX_GREEN } from "constants/index";
import { useAuthContext } from "hooks";
import { useWriterComponentState } from "hooks/useWriterComponentState";

import { PhoneCard } from "./PhoneCard";

interface Props {
  phoneNumber: string;
}

export function MFAReadyState(props: Props) {
  const { phoneNumber } = props;
  const { refreshUser } = useAuthContext();

  const enableMfaWriter = useWriterComponentState({
    errorMessage: "Failed to enable MFA.",
    writeConfig: {
      method: "post",
      url: "/user/mfa/enable",
    },
    errorMessageKey: "message",
  });

  const handleEnableMfa = () => {
    enableMfaWriter.write(() => {
      refreshUser();
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 2, bgcolor: "grey.50" }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Avatar
          sx={{
            bgcolor: "warning.light",
            color: "warning.dark",
            width: 40,
            height: 40,
          }}
        >
          <ShieldIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={500}>
            SMS Authentication
          </Typography>
          <Chip
            label="Ready to Enable"
            color="warning"
            size="small"
            variant="outlined"
          />
        </Box>
      </Stack>
      <Typography color="text.secondary" mb={2} maxWidth={560}>
        Your phone number has been verified. Enable multi-factor authentication
        to add an extra layer of security to your account.
      </Typography>
      <PhoneCard
        label="Verified Number"
        iconColor={EVOTRUX_GREEN}
        phoneNumber={phoneNumber}
        trailingNode={<CheckCircleIcon color="success" fontSize="small" />}
      />
      <Box mt={2}>
        <ErrorTypography error={enableMfaWriter.error} />
        {enableMfaWriter.loading ? (
          <CircularProgress size={24} />
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleEnableMfa}
            startIcon={<ShieldIcon />}
          >
            Enable MFA
          </Button>
        )}
      </Box>
    </Paper>
  );
}
